import { BlueprintInstanceType, Island, projectOntoViewAxis } from '@rta/rta-blueprint-library';

const isIsland = (node: unknown): node is Island => (node as Island | null)?.instanceOf === BlueprintInstanceType.Island;

const ANGLE_TOLERANCE = 1;
// Ends closer together than this along the view axis give no usable direction (a symmetric U).
const PROJECTION_EPSILON = 0.0625;
const normalizeAngle = (angle: number): number => ((angle % 360) + 360) % 360;
const angleClose = (a: number, b: number): boolean => {
	const delta = Math.abs(normalizeAngle(a) - normalizeAngle(b));
	return Math.min(delta, 360 - delta) <= ANGLE_TOLERANCE;
};
const isCardinalPair = (angle: number, a: number, b: number): boolean => angleClose(angle, a) || angleClose(angle, b);
const isCardinal = (angle: number): boolean => isCardinalPair(angle, 0, 180) || isCardinalPair(angle, 90, 270);

// Two cardinal-facing islands connected side-panel-to-side-panel but pointing along different cardinal axes are
// perpendicular to each other, not inline — an L-bend with no Corner island between them (bug #376). A Corner
// island's ~45° facing is non-cardinal, so the guard is inert for the intentional grill → corner → bar traversal.
const isCardinalMismatch = (a: Island, b: Island): boolean => {
	const angleA = normalizeAngle(a.axis.y);
	const angleB = normalizeAngle(b.axis.y);
	if (!isCardinal(angleA) || !isCardinal(angleB)) return false;
	return !angleClose(angleA, angleB);
};

// Island joined through `island`'s own LEFT/RIGHT side panel, or null. Adjacency is decided by which faces connect, never by coordinates.
// When `allowedIds` is given, a real connection whose neighbor falls outside that set is treated as a dead end — this
// keeps the walk from wandering out of a restricted island set (e.g. a joint-countertop group) through a perpendicular
// connection (an island's front/back panel meeting a neighbor's side panel), which is asymmetric and not a two-way
// left/right link, so it can hijack an unrestricted walk away from the islands the caller actually wants.
// A cardinal-to-different-cardinal step is also refused: an L-shape without a Corner island connects two islands
// side-panel-to-side-panel across a 90° facing change, and that neighbor is perpendicular to the row, not inline.
const sideNeighborOf = (island: Island, side: 'left' | 'right', allowedIds?: Set<string>): Island | null => {
	const sidePanel = side === 'left' ? island.frontPanel?.getLeftPanel() : island.frontPanel?.getRightPanel();
	if (!sidePanel) return null;
	const neighbor = island.connections.getConnectionsBySourcePanelId(sidePanel.id)
		.map((connection) => connection.targetIsland)
		.find(isIsland) ?? null;
	if (!neighbor) return null;
	if (allowedIds && !allowedIds.has(neighbor.id)) return null;
	if (isCardinalMismatch(island, neighbor)) return null;
	return neighbor;
};

// Follow LEFT-panel connections outward to the left terminus; the visited set guarantees termination on looping data.
const walkToLeftTerminus = (start: Island, allowedIds?: Set<string>): Island => {
	const visited = new Set<string>([start.id]);
	let terminus = start;
	let next = sideNeighborOf(terminus, 'left', allowedIds);
	while (next && !visited.has(next.id)) {
		visited.add(next.id);
		terminus = next;
		next = sideNeighborOf(terminus, 'left', allowedIds);
	}
	return terminus;
};

// The row's own along-axis world coordinate for a cardinal-facing island: 0/180-facing islands run
// along world X, 90/270-facing islands run along world Z. Same cardinal convention `is-joint-countertop`'s
// getRunCrossCenter uses for its CROSS axis; this reads the axis a row runs ALONG instead. Null for a
// non-cardinal facing (e.g. a Corner's 45-degree panels), which this fallback deliberately skips.
const getAlongAxisPosition = (island: Island): number | null => {
	const angle = normalizeAngle(island.axis.y);
	if (isCardinalPair(angle, 0, 180)) return island.origin.x;
	if (isCardinalPair(angle, 90, 270)) return island.origin.z;
	return null;
};

// The world-space extent an island contributes along ITS OWN along-axis (see above): width for a
// 0/180 facing, depth for a 90/270 facing (a 90-degree leg's "width" as drawn on the page is its depth).
const getAlongAxisWidth = (island: Island): number => {
	const angle = normalizeAngle(island.axis.y);
	return isCardinalPair(angle, 0, 180) ? island.islandSize.width : island.islandSize.depth;
};

/**
 * The inline-connected chain that contains `island`, ordered left-to-right.
 * Walks side-panel (left/right) connections only, so it unfolds a single run — including L/U bends
 * where the chain spans multiple orientations via a non-cardinal Corner island between them — and
 * excludes galley (front/back-to-front/back) partners. A step between two cardinal-facing islands
 * with different cardinal facings is refused (bug #376: an L-shape with no Corner joins them
 * side-panel-to-side-panel across a 90° facing change, and that neighbor is perpendicular to the
 * row, not inline). Islands placed by coordinate only (no formal panel connection anywhere on them)
 * never surface via that walk, so they are appended at whichever end their own along-axis position
 * falls beyond — decided by comparing real coordinates against the chain's own bounds, never a
 * guessed left/right world-space sign — and only when they share the opened island's cardinal facing.
 * @param island the opened island the inline view was launched from
 * @param allowedIds when given, restricts every step of the walk (including coordinate-only appends) to
 * this set — a real connection or coordinate match outside it is treated as a dead end rather than
 * followed. Needed when the caller only wants one sub-run out of a larger connected layout: an island's
 * front/back panel can butt a perpendicular neighbor's side panel (an L-shaped layout with no Corner
 * island), which `sideNeighborOf` still reports as a same-direction link but is one-way (the neighbor's
 * own reciprocal side panel usually isn't connected back), so an unrestricted walk can wander off the
 * caller's intended run and never find its way back to it.
 * @returns the connected run (plus any coordinate-only islands on the same row) in left-to-right order, always including `island`
 */
export const getInlineIslandChain = (island: Island, allowedIds?: Set<string>): Array<Island> => {
	const chain: Array<Island> = [];
	const visited = new Set<string>();
	let next: Island | null = walkToLeftTerminus(island, allowedIds);
	while (next && !visited.has(next.id)) {
		visited.add(next.id);
		chain.push(next);
		next = sideNeighborOf(next, 'right', allowedIds);
	}

	const referenceAngle = normalizeAngle(island.axis.y);
	const chainPositions = chain.map(getAlongAxisPosition).filter((pos): pos is number => pos !== null);
	if (chainPositions.length === 0) return chain;
	const chainMin = Math.min(...chainPositions);
	const chainMax = Math.max(...chainPositions);

	const coordinateOnly = (island.refMap?.islands?.children ?? [])
		.filter((candidate) => !visited.has(candidate.id) && candidate.connections.children.length === 0 && angleClose(normalizeAngle(candidate.axis.y), referenceAngle) && (!allowedIds || allowedIds.has(candidate.id)))
		.map((candidate) => ({ candidate, position: getAlongAxisPosition(candidate) }))
		.filter((entry): entry is { candidate: Island; position: number } => entry.position !== null);

	const before = coordinateOnly.filter((entry) => entry.position < chainMin).sort((a, b) => a.position - b.position).map((entry) => entry.candidate);
	const after = coordinateOnly.filter((entry) => entry.position > chainMax).sort((a, b) => a.position - b.position).map((entry) => entry.candidate);

	return [...before, ...chain, ...after];
};

/**
 * The gap to draw between `island` and its RIGHT-side neighbor `next` in the chain, or 0 when there
 * is no gap to account for. Only one half of a connection pair reports a nonzero `padding` (the
 * source half); the other always reports 0, so the target half's value is read as a fallback rather
 * than assuming which side is source.
 * @param island an island in the chain returned by `getInlineIslandChain`
 * @param next the very next island in that same chain (its coordinate-only append, or a real right-side connection)
 * @returns the real inter-island padding for this side, matching what `getCenterCfn` uses for overhead spacing
 */
export const getInlineIslandPadding = (island: Island, next: Island): number => {
	const sidePanel = island.frontPanel?.getRightPanel();
	const connection = sidePanel
		? island.connections.getConnectionsBySourcePanelId(sidePanel.id).find((c) => c.targetIsland?.id === next.id)
		: null;
	if (connection) return connection.padding || connection.targetConnection?.padding || 0;

	// No formal connection between these two (one or both are coordinate-only) -- fall back to the
	// real gap between their along-axis footprint edges, so a disconnected island draws at roughly
	// its true distance instead of butting flush against the chain.
	const islandPosition = getAlongAxisPosition(island);
	const nextPosition = getAlongAxisPosition(next);
	if (islandPosition === null || nextPosition === null) return 0;
	const gap = nextPosition - islandPosition - (getAlongAxisWidth(island) / 2) - (getAlongAxisWidth(next) / 2);
	return Math.max(gap, 0);
};

export type InlineRunLayoutParams = {
	/** The run in connection order, from `getInlineIslandChain` -- the only thing that can follow a bend. */
	chain: Array<Island>;
	/** The extent each island contributes along the row, which differs by view (panel face vs whole island). */
	widthOf: (island: Island) => number;
	/** World angle of the face being looked at: the island's own facing plus the panel's origin angle. */
	viewAngle: number;
};

export type InlineRunLayout = {
	positionOf: (island: Island) => number;
	totalWidth: number;
	/** The run in the order the viewer reads it, left to right. */
	ordered: Array<Island>;
};

// Does the walk already run left-to-right for this viewer, or does it come back at them? Decided from
// real coordinates (projectOntoViewAxis), never a facing compared to a constant. Only the DIRECTION is
// a coordinate question; the ORDER has to stay the walk's, because a run can bend through a Corner
// island and a single projection cannot order a path that changes axis -- it interleaves a U's legs.
// The first ADJACENT pair that separates along the view axis decides it: comparing the run's two far
// ENDS instead ties on a symmetric U (both ends at the same x), which would leave it unreversed when
// viewed from behind. Adjacent islands can only tie if they sit at the same point, so the scan
// continues to the next pair; a run with no separated pair at all has no direction to read.
const runsRightToLeft = (chain: Array<Island>, viewAngle: number): boolean => {
	for (let index = 1; index < chain.length; index++) {
		const from = projectOntoViewAxis({ point: chain[index - 1].origin, viewAngle });
		const to = projectOntoViewAxis({ point: chain[index].origin, viewAngle });
		if (Math.abs(to - from) > PROJECTION_EPSILON) return to < from;
	}

	return false;
};

/**
 * Lays a run out as the viewer sees it: the walk's own order (reversed when the walk runs away from the
 * viewer's right), spaced by cumulative width plus the real inter-island padding. The run is UNFOLDED,
 * so an L/U bend draws straight rather than at true world spacing.
 *
 * This is deliberately the only place the question is answered. Every view of a run -- front elevation,
 * back elevation, countertop, the blueprint joint-countertop sheet -- is the same geometry seen from a
 * different angle, so each passes its own `viewAngle` and nothing else differs. The mirror flags this
 * replaced guessed direction from `axis.y === 180`, and two callers guessed differently and drew the
 * same run in opposite directions (#646 / #656).
 * @param params the run in connection order, how wide each island is in this view, and the angle viewed
 * @returns where to draw each island along the row, the row's total width, and the viewer-order run
 */
export const layoutInlineRun = (params: InlineRunLayoutParams): InlineRunLayout => {
	const { chain, widthOf, viewAngle } = params;

	const ordered = runsRightToLeft(chain, viewAngle) ? [...chain].reverse() : [...chain];

	const centerByIslandId = new Map<string, number>();
	let runningX = 0;

	ordered.forEach((island, index) => {
		// same butting-island gap getCenterCfn applies for overhead spacing, unfolded along this row's own axis
		if (index > 0) runningX += getInlineIslandPadding(ordered[index - 1], island);
		const width = widthOf(island);
		centerByIslandId.set(island.id, runningX + (width / 2));
		runningX += width;
	});

	return {
		positionOf: (island: Island): number => centerByIslandId.get(island.id) ?? 0,
		totalWidth: runningX,
		ordered
	};
};
